import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DgbLogoPrimary } from '@/components/brand-logo';
import { Button } from '@/components/ui/button';
import { unsubscribe } from '../api';

const PATH = /^\/onboarding\/unsubscribe\/([^/]+)\/?$/;

/** The token of an unsubscribe address, or `null` when the path is any other page. */
export function unsubscribeTokenOf(pathname: string): string | null {
  const match = PATH.exec(pathname);
  if (match?.[1] === undefined) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

type Phase = 'ask' | 'busy' | 'done' | 'error';

/**
 * `/onboarding/unsubscribe/:token`, reachable WITHOUT signing in (#739): the brand mark, one short
 * text and one button. The link's token is the only proof; the button posts it.
 */
export function UnsubscribePage({ token }: { readonly token: string }) {
  const { t } = useTranslation('communications');
  const [phase, setPhase] = useState<Phase>('ask');

  const run = async () => {
    setPhase('busy');
    const result = await unsubscribe(token);
    setPhase(result);
  };

  const asking = phase !== 'done';
  return (
    <main className="bg-background text-foreground flex min-h-dvh flex-col items-center justify-center gap-6 p-6 text-center">
      <DgbLogoPrimary className="h-12" />
      <div className="flex max-w-[26rem] flex-col items-center gap-4">
        {asking ? (
          <>
            <h1 className="text-xl font-bold">{t('unsubscribe.title')}</h1>
            <p className="text-muted-foreground text-base">{t('unsubscribe.text')}</p>
            <Button
              type="button"
              size="lg"
              className="min-h-11 w-full"
              disabled={phase === 'busy'}
              onClick={() => void run()}
            >
              {phase === 'error' ? t('common.retry') : t('unsubscribe.button')}
            </Button>
            {phase === 'error' ? (
              <p role="alert" className="text-destructive text-sm">
                {t('unsubscribe.error')}
              </p>
            ) : null}
          </>
        ) : (
          <p role="status" className="text-lg font-semibold">
            {t('unsubscribe.done')}
          </p>
        )}
      </div>
    </main>
  );
}
